import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";
import type { CallContact } from "services/dialstack";

import { phonesMatch } from "./phone-match.util";

// Set on the pop for a transferred call's receiving leg. That leg is a NEW
// provider call placed from the colleague's extension, so the pop's own
// `fromNumber` is the extension and the person actually on the line is here.
export type PopTransfer = {
  byName: string | null;
  originalFromNumber: string | null;
  originalFromName: string | null;
};

// One websocket `call.incoming` pop (dialstack/notifications.py). The backend
// has already matched the caller to patients — patient_ids carries EVERY match
// (matched on phone / secondary_phone / responsible_party_phone).
export type IncomingCallPop = {
  // Provider call id (Call.dialstack_call_id) — what the SDK's Call.id carries.
  callId: string;
  // Backend Call.id; what the attach endpoint needs. Null from a backend that
  // predates it; while it is null the unknown-caller actions stay disabled and
  // the softphone looks the call up by provider id (use-pop-recovery.hook.ts).
  backendCallId: number | null;
  clinicId: number | null;
  fromNumber: string;
  fromName: string | null;
  matchedOn: string | null;
  patientIds: number[];
  contact: CallContact | null;
  transfer?: PopTransfer | null;
  receivedAt: number;
};

// Who is actually on the line: the original caller on a transferred leg, the
// pop's own caller otherwise.
export const popCallerNumber = (pop: IncomingCallPop) =>
  pop.transfer?.originalFromNumber ?? pop.fromNumber;

export const popCallerName = (pop: IncomingCallPop) =>
  pop.transfer ? pop.transfer.originalFromName : pop.fromName;

type IncomingCallPopState = {
  pops: IncomingCallPop[];
};

// Pops are pruned by age rather than by call events: the SDK's incoming call
// and the websocket pop share no id, so consumers join them by phone number
// while the pop is fresh. 120s comfortably covers ring time.
export const POP_TTL_MS = 120_000;
const MAX_POPS = 5;

const initialState: IncomingCallPopState = { pops: [] };

export const incomingCallPopSlice = createSlice({
  name: "incomingCallPop",
  initialState,
  reducers: {
    // Upsert by callId so a re-delivered webhook/pop stays one entry. A
    // transferred leg gets two pops (`call.incoming` and `call.transfer`) in
    // no guaranteed order, and only the second knows it is a transfer — so a
    // later plain pop must not erase that.
    popReceived: (state, action: PayloadAction<IncomingCallPop>) => {
      const existing = state.pops.find(
        (pop) => pop.callId === action.payload.callId
      );
      state.pops = [
        {
          ...action.payload,
          transfer: action.payload.transfer ?? existing?.transfer ?? null
        },
        ...state.pops.filter((pop) => pop.callId !== action.payload.callId)
      ].slice(0, MAX_POPS);
    },
    // A pop built from a later frame — the status frame of a call taken on
    // a handset (ENG-2605) — for a call this browser never saw ring. It only
    // fills a gap: the ring-time pop knows more (a transfer's original
    // caller) and always wins.
    popBackfilled: (state, action: PayloadAction<IncomingCallPop>) => {
      if (state.pops.some((pop) => pop.callId === action.payload.callId))
        return;
      state.pops = [action.payload, ...state.pops].slice(0, MAX_POPS);
    },
    popsCleared: (state) => {
      state.pops = [];
    }
  }
});

export const { popReceived, popBackfilled, popsCleared } =
  incomingCallPopSlice.actions;

const selectPops = (state: RootState) => state.incomingCallPop.pops;

export const selectFreshPops = (state: RootState, now: number) =>
  selectPops(state).filter((pop) => now - pop.receivedAt <= POP_TTL_MS);

export const selectFreshPopForNumber = (
  state: RootState,
  phoneNumber: string,
  now: number
) =>
  selectFreshPops(state, now).find((pop) =>
    phonesMatch(pop.fromNumber, phoneNumber)
  );

export default incomingCallPopSlice.reducer;
